import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { createChatApi, deleteChatApi, fetchProjectChatsApi, updateChatApi } from '../../api/chats.api';
import { DEFAULT_LLM_MODEL_KEY } from '../../constants/llmModels';
import { getNextTaskTitle } from '../../utils/taskTitles';

const ensureProjectChatList = (state, projectId) => {
  if (!state.byProjectId[projectId]) {
    state.byProjectId[projectId] = [];
  }
};

const getProjectIdFromArg = (arg) => (typeof arg === 'object' ? arg.projectId : arg);
const getTitleFromArg = (arg) => (typeof arg === 'object' ? arg.title || '' : '');
const getContextFromArg = (arg) => (typeof arg === 'object' ? arg.context || '' : '');

export const fetchProjectChats = createAsyncThunk('chats/fetchProjectChats', async (projectId) => {
  const chats = await fetchProjectChatsApi(projectId);
  return { projectId, chats };
});

export const createProjectChat = createAsyncThunk(
  'chats/createProjectChat',
  async ({ projectId, title = '', context = '', model = '' }) => {
    const chat = await createChatApi(projectId, { title, context, model });
    return { projectId, chat };
  }
);

export const updateProjectChat = createAsyncThunk(
  'chats/updateProjectChat',
  async ({ projectId, chatId, title = '', context = '' }) => {
    const chat = await updateChatApi(chatId, { title, context });
    return { projectId, chat };
  }
);

export const deleteProjectChat = createAsyncThunk(
  'chats/deleteProjectChat',
  async ({ projectId, chatId }, { getState }) => {
    await deleteChatApi(chatId);

    const remainingChats = (getState().chats.byProjectId[projectId] || []).filter((chat) => chat.id !== chatId);
    const nextActiveChatId = remainingChats[0]?.id || null;

    return { projectId, chatId, nextActiveChatId };
  }
);

export const ensureProjectChats = createAsyncThunk(
  'chats/ensureProjectChats',
  async (arg, { dispatch, getState }) => {
    const projectId = getProjectIdFromArg(arg);
    const title = getTitleFromArg(arg);
    const context = getContextFromArg(arg);
    const preferredChatId = typeof arg === 'object' ? arg.preferredChatId : null;

    let chats = [];

    try {
      const action = await dispatch(fetchProjectChats(projectId));
      if (fetchProjectChats.fulfilled.match(action)) {
        chats = action.payload.chats;
      }
    } catch (error) {
      chats = [];
    }

    if (chats.length === 0) {
      const language = getState().app?.language || 'fa';
      const createAction = await dispatch(
        createProjectChat({
          projectId,
          title: title || getNextTaskTitle(language, 0),
          context,
          model: DEFAULT_LLM_MODEL_KEY
        })
      );
      if (createProjectChat.fulfilled.match(createAction)) {
        chats = [createAction.payload.chat];
      }
    }

    const activeChatId =
      (preferredChatId && chats.some((chat) => chat.id === preferredChatId) ? preferredChatId : null) ||
      getState().chats.activeChatIdByProjectId[projectId] ||
      chats[0]?.id ||
      null;

    return { projectId, chats, activeChatId };
  }
);

const chatsSlice = createSlice({
  name: 'chats',
  initialState: {
    byProjectId: {},
    activeChatIdByProjectId: {},
    loading: false,
    error: null
  },
  reducers: {
    setActiveChatId: (state, action) => {
      const { projectId, chatId } = action.payload;
      state.activeChatIdByProjectId[projectId] = chatId;
    },
    clearProjectChats: (state, action) => {
      const projectId = action.payload;
      delete state.byProjectId[projectId];
      delete state.activeChatIdByProjectId[projectId];
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProjectChats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProjectChats.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const { projectId, chats } = action.payload;
        ensureProjectChatList(state, projectId);
        state.byProjectId[projectId] = chats;

        if (!state.activeChatIdByProjectId[projectId] && chats[0]?.id) {
          state.activeChatIdByProjectId[projectId] = chats[0].id;
        }
      })
      .addCase(fetchProjectChats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Chat list request failed.';
      })
      .addCase(createProjectChat.fulfilled, (state, action) => {
        state.error = null;
        const { projectId, chat } = action.payload;
        ensureProjectChatList(state, projectId);
        state.byProjectId[projectId] = [chat, ...state.byProjectId[projectId].filter((item) => item.id !== chat.id)];
        state.activeChatIdByProjectId[projectId] = chat.id;
      })
      .addCase(createProjectChat.rejected, (state, action) => {
        state.error = action.error.message || 'Create chat failed.';
      })
      .addCase(updateProjectChat.fulfilled, (state, action) => {
        state.error = null;
        const { projectId, chat } = action.payload;
        ensureProjectChatList(state, projectId);
        state.byProjectId[projectId] = state.byProjectId[projectId].map((item) =>
          item.id === chat.id ? chat : item
        );
      })
      .addCase(updateProjectChat.rejected, (state, action) => {
        state.error = action.error.message || 'Update chat failed.';
      })
      .addCase(deleteProjectChat.fulfilled, (state, action) => {
        state.error = null;
        const { projectId, chatId, nextActiveChatId } = action.payload;
        ensureProjectChatList(state, projectId);
        state.byProjectId[projectId] = state.byProjectId[projectId].filter((item) => item.id !== chatId);

        if (state.activeChatIdByProjectId[projectId] === chatId) {
          state.activeChatIdByProjectId[projectId] = nextActiveChatId;
        }
      })
      .addCase(deleteProjectChat.rejected, (state, action) => {
        state.error = action.error.message || 'Delete chat failed.';
      })
      .addCase(ensureProjectChats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(ensureProjectChats.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const { projectId, chats, activeChatId } = action.payload;
        ensureProjectChatList(state, projectId);
        state.byProjectId[projectId] = chats;

        if (activeChatId) {
          state.activeChatIdByProjectId[projectId] = activeChatId;
        }
      })
      .addCase(ensureProjectChats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Chat setup failed.';
      });
  }
});

export const { setActiveChatId, clearProjectChats } = chatsSlice.actions;
export default chatsSlice.reducer;
